import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  TextField,
} from "@mui/material";
import {
  CUSTOM_TYPE,
  isPredefinedType,
  toTypeCode,
  typeOptions,
} from "./consentTemplateModel";
import { ALL_SPECIALITIES_LABEL } from "./consentSimpleModel";

const ConsentBasicInfoForm = ({
  value,
  errors = {},
  meta,
  specialityOptions = [],
  onChange,
}) => {
  const options = typeOptions(meta);
  const custom = Boolean(value.type) && !isPredefinedType(value.type);
  const selected = custom ? CUSTOM_TYPE : value.type || "";
  const allSpecialities = value.allSpecialities === true;

  const set = (patch) => onChange({ ...value, ...patch });

  const handleType = (code) => {
    if (code === CUSTOM_TYPE) {
      // Keep whatever wording is already typed; the code follows the label.
      set({
        type: toTypeCode(value.typeLabel || "CUSTOM") || "CUSTOM",
        typeLabel: value.typeLabel || "",
      });
      return;
    }

    const option = options.find((item) => item.code === code);

    set({
      type: code,
      typeLabel: "",
      name: value.name?.trim() ? value.name : option?.label || "",
    });
  };

  return (
    <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr" }}>
      <Box sx={{ gridColumn: "1 / -1" }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          options={specialityOptions}
          value={value.speciality || []}
          disabled={allSpecialities}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, item) => option._id === item._id}
          onChange={(event, next) => set({ speciality: next })}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Specialities"
              error={Boolean(errors.speciality)}
              placeholder={
                allSpecialities ? ALL_SPECIALITIES_LABEL : "Select specialities"
              }
            />
          )}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={allSpecialities}
              inputProps={{ "data-testid": "consent-all-specialities" }}
              onChange={(event) => {
                const checked = event.target.checked;
                set({
                  allSpecialities: checked,
                  ...(checked ? { speciality: [] } : {}),
                });
              }}
            />
          }
          label={`${ALL_SPECIALITIES_LABEL} (available to every speciality)`}
        />

        {errors.speciality ? (
          <FormHelperText error>{errors.speciality}</FormHelperText>
        ) : null}
      </Box>

      <TextField
        size="small"
        label="Consent Name"
        required
        value={value.name || ""}
        error={Boolean(errors.name)}
        helperText={errors.name || "Shown in the consent list"}
        onChange={(event) => set({ name: event.target.value })}
      />

      <TextField
        select
        size="small"
        label="Consent Format"
        required
        value={selected}
        error={Boolean(errors.type)}
        helperText={errors.type || " "}
        onChange={(event) => handleType(event.target.value)}
      >
        {options.map((option) => (
          <MenuItem key={option.code} value={option.code}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {custom ? (
        <>
          <TextField
            size="small"
            label="Custom format name"
            value={value.typeLabel || ""}
            helperText="e.g. Teleconsultation Consent"
            onChange={(event) =>
              set({
                typeLabel: event.target.value,
                type: toTypeCode(event.target.value) || "CUSTOM",
              })
            }
          />
          <TextField
            size="small"
            label="Format code"
            value={value.type || ""}
            helperText="Derived from the name; edit if you need a specific code"
            onChange={(event) => set({ type: toTypeCode(event.target.value) })}
          />
        </>
      ) : null}
    </Box>
  );
};

export default ConsentBasicInfoForm;
